// ============================================================================
// THE NATIVE RECORDER, SEEN FROM THE WEB APP — REC-1c, the web half.
//
// WHAT IT IS. `apps/desktop/plugins/lifeos-recorder` is a Tauri v2 plugin: on Android it runs a
// foreground service with a `MediaRecorder` behind it, arms exact alarms for class time, and
// keeps a JSON sidecar next to every file so an interrupted lecture can still say what it was.
// docs/NATIVE-SPIKE.md proved that shell records for 110 minutes with the screen locked on the
// owner's S24. Since REC-1 PR-C the APK loads THIS APP rather than a probe page, so everything
// below now has a real other end — on one platform, in one build. It still feature-detects and
// still degrades out loud, because this same file runs in a browser tab on every other platform.
//
// THE ONE RULE. Nothing here throws at a caller and nothing here pretends. Every call answers a
// `NativeResult`: either `{ available: true, value }` — the plugin actually answered — or
// `{ available: false, reason }` — a sentence the screen can show. A recorder that is not there
// must never look like a recorder that is idle, because "not recording" and "cannot record" send
// the owner into a lecture with two different beliefs about whether his phone is listening.
//
// WHAT `nativeRecorderAvailable()` CAN AND CANNOT KNOW. It answers whether the Tauri IPC bridge
// is on this page. That is the whole of what is knowable synchronously: whether the plugin is
// registered, and whether the capability grants these commands, is only discoverable by invoking
// one, which is asynchronous. So a screen confirms with `nativeRecorderStatus()` before it offers
// a Record button, and this function is the cheap gate in front of that — never the proof.
//
// THE BYTES ARE NO LONGER THE GAP. PR 116 wrote down two possible contracts for getting a finished
// recording out of the app's private storage; PR-C shipped option (b) — a `read` command on the
// recorder plugin itself, rather than registering a general-purpose filesystem plugin that would
// hand this webview every file the app owns. Only `files/recordings` is any of this app's
// business, and the Kotlin side enforces that with a canonical-path check.
//
// IT IS CHUNKED, and the loop in `readNativeRecording` is the whole reason: the Android IPC
// carries JSON, so a 28 MB lecture cannot cross it in one piece without a base64 string bigger
// than the recording. 1 MiB at a time, assembled into one Blob, and a mismatch between the bytes
// received and the bytes the plugin said the file had is a REFUSAL — a short recording silently
// uploaded as if it were whole is the failure this arc exists to prevent.
//
// THE COMMAND NAMES AND THE `payload` WRAPPER are the plugin's, verbatim: they are generated from
// `apps/desktop/plugins/lifeos-recorder/build.rs`'s COMMANDS list, and the Android capability
// (`apps/desktop/src-tauri/capabilities/android.json`) must allow each one by name. If one moves,
// all three move.
// ============================================================================

import { DEFAULT_END_OFFSET_MINUTES } from './class-labeling'

/** What `plugin:lifeos-recorder|status` and `|start` answer. Optional fields are genuinely absent. */
export interface NativeRecorderStatus {
  recording: boolean
  /** ISO-8601. Present only while recording. */
  startedAt?: string
  /** The absolute path being written to. Present only while recording. */
  path?: string
  /** The course this recording will be filed under. Present only when one was named. */
  courseCode?: string | null
  /**
   * True when an ALARM started this recording, not the owner.
   *
   * It is what makes "a manual recording overrides a scheduled one" observable from here: a stop
   * alarm ends only a recording this flag is true for.
   */
  scheduled?: boolean
  /**
   * Whether Android will let LifeOS take the screen over at class time (API 34+ gates it).
   * ABSENT below API 34, where the question does not exist — not `false`.
   */
  fullScreenIntentAllowed?: boolean
  /** Whether the app may set EXACT alarms. `false` means class alarms may fire late. */
  exactAlarmsAllowed?: boolean
  /**
   * When Android first fed the recorder silence (API 29+).
   *
   * ABSENT IS NOT `false`. Below API 29 the plugin cannot ask, and reporting `false` there would
   * turn "nobody knows" into "we checked and it was fine".
   */
  audioFocusLostAt?: string
}

/** What `plugin:lifeos-recorder|stop` answers. Every number is measured, not predicted. */
export interface NativeRecordingFile {
  path: string
  durationMs: number
  bytes: number
  /**
   * Present when the PHONE'S OWN UPLOADER took this recording at Stop (the phone holds an upload
   * token). Absent means nothing native will send it, and the web queue is the one to hand it over.
   */
  upload?: NativeUploadState | null
}

/**
 * The phone's own uploader's record of one recording (the plugin's WorkManager uploader, 2026-09-24).
 *
 * ITS PRESENCE IS THE OWNERSHIP MARK — see `nativeUploaderOwns`. The four words are the phone's:
 *   queued    — waiting for a network, or for its next try (`lastError` says why the last one did not land)
 *   uploading — an attempt is running
 *   received  — the server confirmed it (`received: true` under this recording's own clientUploadId);
 *               the phone deletes its copy 24 hours later, exactly as after a web hand-off
 *   failed    — trying again would get the same answer; `lastError` is the Worker's own sentence
 */
export interface NativeUploadState {
  state: 'queued' | 'uploading' | 'received' | 'failed'
  attempts: number
  jobId?: string | null
  lectureId?: string | null
  lastError?: string | null
  /** When `state` last changed. */
  at?: string | null
}

/** What `tokenstate` / `settoken` answer. The token itself is never a field of anything. */
export interface NativeUploadTokenState {
  present: boolean
  label?: string | null
  /** When this phone was given it. */
  since?: string | null
}

/**
 * Either the plugin answered, or it did not and here is why in words.
 *
 * There is deliberately no third shape for "it answered with an error": an Android-side refusal
 * (microphone denied, no storage) arrives as `{ available: false, reason: <the plugin's own
 * sentence> }`, because from the screen's point of view both mean the same thing — this phone is
 * not recording and here is the reason to show.
 */
export type NativeResult<T> =
  | { available: true; value: T }
  | { available: false; reason: string }

type Invoke = (command: string, args?: unknown) => Promise<unknown>

interface TauriInternals {
  invoke?: Invoke
}
interface TauriGlobal {
  core?: { invoke?: Invoke }
}

/** Exactly the two places Tauri v2 puts `invoke`, in the order the spike's bindings try them. */
function bridge(): Invoke | null {
  try {
    const internals = (globalThis as { __TAURI_INTERNALS__?: TauriInternals }).__TAURI_INTERNALS__
    if (internals && typeof internals.invoke === 'function') return internals.invoke.bind(internals)
    const global = (globalThis as { __TAURI__?: TauriGlobal }).__TAURI__
    if (global?.core && typeof global.core.invoke === 'function') {
      return global.core.invoke.bind(global.core)
    }
  } catch {
    // A getter that throws (a hardened page, a sandboxed iframe) is "no bridge", not a crash.
  }
  return null
}

/** The sentence every screen shows when this is a browser tab rather than the app. */
export const NO_NATIVE_SHELL = 'Record on this phone needs the LifeOS app'

/**
 * Is the Tauri IPC bridge on this page?
 *
 * Read it live, never cached at module scope: the bridge is injected by the shell's bootstrap and
 * a value captured at import time would be a guess about ordering. It is also what makes this
 * testable — a test installs a bridge and takes it away again.
 */
export function nativeRecorderAvailable(): boolean {
  return bridge() !== null
}

async function call<T>(command: string, args?: unknown): Promise<NativeResult<T>> {
  const invoke = bridge()
  if (!invoke) return { available: false, reason: NO_NATIVE_SHELL }
  try {
    const value = (await invoke(command, args)) as T
    return { available: true, value }
  } catch (error) {
    // An unregistered command, a capability that does not grant it, and a microphone the owner
    // denied all arrive here. The plugin's own words are better than any sentence written here.
    return { available: false, reason: reasonOf(error) }
  }
}

/**
 * The same bridge call, for the plugin's other adapters (`native-notify.ts`). One place decides
 * what "the plugin answered" and "it did not, and here is why" look like.
 */
export function callNativePlugin<T>(command: string, args?: unknown): Promise<NativeResult<T>> {
  return call<T>(command, args)
}

function reasonOf(error: unknown): string {
  if (typeof error === 'string' && error.trim()) return error
  if (error instanceof Error && error.message.trim()) return error.message
  return 'The LifeOS app refused to record and gave no reason.'
}

/**
 * Start recording, with the course the schedule says this is.
 *
 * The course code is passed through verbatim: the plugin puts it in the notification and slugs it
 * into the filename. Passing `null` is a real choice (no class matched), never a placeholder.
 */
export function startNativeRecording(
  courseCode?: string | null,
): Promise<NativeResult<NativeRecorderStatus>> {
  return call<NativeRecorderStatus>('plugin:lifeos-recorder|start', {
    payload: { courseCode: courseCode ?? null },
  })
}

/** Stop and finalize. `bytes` is the file's real length, read off the finished container. */
export function stopNativeRecording(): Promise<NativeResult<NativeRecordingFile>> {
  return call<NativeRecordingFile>('plugin:lifeos-recorder|stop')
}

/** What the service is doing right now. This is also the plugin-present probe. */
export function nativeRecorderStatus(): Promise<NativeResult<NativeRecorderStatus>> {
  return call<NativeRecorderStatus>('plugin:lifeos-recorder|status')
}

// ── the bytes ───────────────────────────────────────────────────────────────

/** One slice of a recording, as `plugin:lifeos-recorder|read` answers it. */
interface NativeReadChunk {
  /** This slice, base64. Empty at end-of-file. */
  base64: string
  offset: number
  chunkBytes: number
  /** The whole file's length, so the caller can check it got all of it. */
  totalBytes: number
  eof: boolean
}

/**
 * A runaway guard, not a size limit. At 1 MiB a chunk this allows a 4 GiB recording — about 140
 * hours of 64 kbps audio. If the loop ever reaches it, the plugin is not making progress and
 * saying so beats spinning.
 */
const MAX_CHUNKS = 4096

/**
 * Read a finished recording's bytes so the upload queue can hold them.
 *
 * WHAT IT COSTS TO GET THIS WRONG is the reason for every check below. The queue takes whatever
 * Blob it is handed and, once the server acknowledges it, the native file is eventually deleted.
 * So a truncated read that looked successful would destroy a lecture. Three refusals guard it:
 * an empty file, a chunk that decodes to nothing while the plugin still says there is more, and a
 * final length that does not equal the length the plugin reported. Each answers
 * `{ available: false, reason }`, which leaves the recording exactly where it is.
 */
export async function readNativeRecording(path: string): Promise<NativeResult<Blob>> {
  const parts: Uint8Array[] = []
  let offset = 0
  let total: number | null = null

  for (let chunks = 0; chunks < MAX_CHUNKS; chunks++) {
    const answer = await call<NativeReadChunk>('plugin:lifeos-recorder|read', {
      payload: { path, offset },
    })
    if (!answer.available) return answer

    const chunk = answer.value
    if (typeof chunk?.base64 !== 'string' || typeof chunk?.totalBytes !== 'number') {
      return {
        available: false,
        reason: 'The app answered with something that is not the recording’s bytes.',
      }
    }
    total = chunk.totalBytes
    if (total === 0) {
      return {
        available: false,
        reason: 'That recording is empty — nothing was ever written to it.',
      }
    }

    const bytes = decodeBase64(chunk.base64)
    if (!bytes) {
      return { available: false, reason: 'That recording could not be decoded on this phone.' }
    }
    if (bytes.length > 0) {
      parts.push(bytes)
      offset += bytes.length
    }
    if (chunk.eof) break
    if (bytes.length === 0) {
      // Not at the end, and nothing came back. Another pass would ask the same question forever.
      return { available: false, reason: 'The app stopped part-way through that recording.' }
    }
  }

  if (total === null || offset !== total) {
    return {
      available: false,
      reason: `Only ${offset} of ${total ?? '?'} bytes came back, so nothing was queued.`,
    }
  }
  // MPEG-4 audio, which is what the plugin records (AAC in MPEG-4, 64 kbps mono — NATIVE-SPIKE).
  return { available: true, value: new Blob(parts as BlobPart[], { type: 'audio/mp4' }) }
}

/** base64 → bytes, or null. A malformed chunk is refused, never turned into a shorter recording. */
function decodeBase64(text: string): Uint8Array | null {
  if (text.length === 0) return new Uint8Array(0)
  try {
    const binary = atob(text)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return bytes
  } catch {
    return null
  }
}

// ── the list on the phone ───────────────────────────────────────────────────

/**
 * One recording in the app's own storage, as `plugin:lifeos-recorder|list` reports it.
 *
 * `finalized: false, recording: false` IS THE CRASH CASE: the process died mid-lecture, the MPEG-4
 * container was never closed, and the bytes are still there. The plugin does not delete it and
 * cannot repair it — rebuilding a missing `moov` index means re-parsing every audio frame, which
 * is a transcoder, not a recorder. What is recoverable is stated (the byte count, the start time)
 * and the file stays put.
 *
 * Nullable rather than optional throughout: these come off a Kotlin `JSObject` where an absent
 * fact is an absent key, but the plugin also has paths that send an explicit `null`, and a type
 * that accepts both is the one that never lies about which arrived.
 */
export interface NativeRecordingEntry {
  path: string
  bytes: number
  startedAt?: string | null
  /** Measured at stop. Absent for an interrupted file — no length is derived from a byte count. */
  durationMs?: number | null
  finalized: boolean
  courseCode?: string | null
  /** True when an alarm started it rather than the owner. */
  scheduled: boolean
  /** True for the file being written right now. */
  recording: boolean
  /** When the server's receipt was recorded. The file is deleted 24 h after this, never before. */
  handedOffAt?: string | null
  receiptClientUploadId?: string | null
  /** True while a "Not in class" discard sits inside its ten-second undo window. */
  pendingDiscard: boolean
  /** The phone's own uploader's record. Absent when the uploader does not own this file. */
  upload?: NativeUploadState | null
}

/**
 * WHO SENDS THIS RECORDING — THE ONE RULE, stated here and nowhere else.
 *
 * Two things can upload a native recording: the web queue (IndexedDB, only while LifeOS is open)
 * and the phone's own uploader (WorkManager, app closed or not). Both sending one lecture would be
 * two jobs and two lectures for one class, so exactly one of them owns each file:
 *
 *   THE PHONE'S UPLOADER OWNS IT when
 *     · the file carries an upload state — the plugin writes one, in the same sidecar write as
 *       `finishedAt` and before the file stops reading as "recording", whenever a Stop finishes it
 *       on a phone that holds an upload token; or
 *     · the phone holds an upload token and the recording STARTED after the token was handed over
 *       — any Stop of such a recording marks it, so this closes the gap before the mark is read.
 *   OTHERWISE THE WEB QUEUE OWNS IT, exactly as before the uploader existed — including every
 *   recording made before the token was handed over, which the phone never adopts.
 *
 * The web side applies it in two places: the Stop button's hand-off (it defers when this is true)
 * and the alarm-recording pick-up sweep (`alarmRecordingsToImport` skips these).
 */
export function nativeUploaderOwns(
  recording: Pick<NativeRecordingEntry, 'upload' | 'startedAt'>,
  token: NativeUploadTokenState | null | undefined,
): boolean {
  if (recording.upload) return true
  if (!token?.present || !token.since) return false
  const since = Date.parse(token.since)
  const started = recording.startedAt ? Date.parse(recording.startedAt) : Number.NaN
  return Number.isFinite(since) && Number.isFinite(started) && started >= since
}

/**
 * What the phone says about its upload token: `{ present, label?, since? }`. Never the token.
 *
 * An app build older than the uploader answers `available: false` (the command does not exist),
 * which the screen treats as "this build cannot upload on its own" — not as "no token".
 */
export async function nativeUploadTokenState(): Promise<NativeResult<NativeUploadTokenState>> {
  return normalizeToken(await call<NativeUploadTokenState>('plugin:lifeos-recorder|tokenstate'))
}

/**
 * Hand the phone a device capture token (minted with `POST /api/capture-tokens`) and the API it
 * belongs to. The plugin stores it encrypted and answers only `{ present, label?, since? }`.
 */
export async function setNativeUploadToken(
  token: string,
  apiBase: string,
  label: string,
): Promise<NativeResult<NativeUploadTokenState>> {
  return normalizeToken(
    await call<NativeUploadTokenState>('plugin:lifeos-recorder|settoken', {
      payload: { token, apiBase, label },
    }),
  )
}

function normalizeToken(
  answer: NativeResult<NativeUploadTokenState>,
): NativeResult<NativeUploadTokenState> {
  if (!answer.available) return answer
  if (typeof answer.value?.present !== 'boolean') {
    return { available: false, reason: 'The app answered with something that is not a token state.' }
  }
  return answer
}

/**
 * What happened to one class-time PROMPT.
 *
 * WHY THERE IS A PROMPT. Android 14+ will not let an alarm open the microphone by itself — the
 * owner's S24 crashed at every class alarm until the phone was changed to ASK (2026-09-24,
 * docs/NATIVE-SPIKE.md). So at listed start + Berkeley Time the phone shows "COGSCI 175 started —
 * tap to record", and one tap starts the recording. Unanswered, it re-alerts every 2 minutes
 * until it is answered or the class ends (2026-11-01). A prompt nobody answered has no file, so this
 * is the only place "prompt not answered" can be read back from the phone.
 */
export interface NativeClassPrompt {
  courseCode: string
  /** When the class alarm fired (listed start + Berkeley Time). Absent when the alarm did not say. */
  startsAt?: string | null
  /** The class's stop alarm (listed end + the end offset). An unanswered prompt expires here. */
  endsAt: string
  shownAt?: string | null
  outcome: 'open' | 'started' | 'not-in-class' | 'not-answered' | 'start-failed'
  answeredAt?: string | null
  /** Android's own words for a failed start, or why a prompt closed unanswered. */
  reason?: string | null
}

/** One read of the phone's list: its files, and its class prompts. */
export interface NativePhoneList {
  recordings: NativeRecordingEntry[]
  /** NULL when this build of the app does not report prompts — absent is not "none". */
  prompts: NativeClassPrompt[] | null
}

/** The phone's files and its class prompts, in one call. */
export async function listNativePhone(): Promise<NativeResult<NativePhoneList>> {
  const answer = await call<{ recordings?: NativeRecordingEntry[]; prompts?: NativeClassPrompt[] }>(
    'plugin:lifeos-recorder|list',
  )
  if (!answer.available) return answer
  const recordings = answer.value?.recordings
  if (!Array.isArray(recordings)) {
    return { available: false, reason: 'The app answered with something that is not a list.' }
  }
  const prompts = answer.value?.prompts
  return { available: true, value: { recordings, prompts: Array.isArray(prompts) ? prompts : null } }
}

/** Everything on the phone, newest first. Management, and the crash-recovery view. */
export async function listNativeRecordings(): Promise<NativeResult<NativeRecordingEntry[]>> {
  const answer = await listNativePhone()
  if (!answer.available) return answer
  return { available: true, value: answer.value.recordings }
}

/** Forget a recording on the phone. The plugin refuses a path outside its own directory. */
export async function deleteNativeRecording(path: string): Promise<NativeResult<boolean>> {
  const answer = await call<{ deleted?: boolean }>('plugin:lifeos-recorder|delete', {
    payload: { path },
  })
  if (!answer.available) return answer
  return { available: true, value: answer.value?.deleted === true }
}

/**
 * Tell the phone the SERVER has this recording — the only thing that licenses deleting it.
 *
 * `receiptClientUploadId` is the id the server itself named (`QueueEntry.receiptClientUploadId`),
 * not this app's copy of what it sent. The native side stores it, and deletes the file 24 hours
 * later. Calling this without a real receipt would be how a lecture disappears, so the plugin
 * refuses an empty one rather than treating it as "close enough".
 */
export async function handOffNativeRecording(
  path: string,
  receiptClientUploadId: string,
): Promise<NativeResult<string | null>> {
  const answer = await call<{ handedOff?: boolean; handedOffAt?: string | null }>(
    'plugin:lifeos-recorder|handoff',
    { payload: { path, receiptClientUploadId } },
  )
  if (!answer.available) return answer
  return { available: true, value: answer.value?.handedOffAt ?? null }
}

// ── the class alarms ────────────────────────────────────────────────────────

/**
 * One class meeting, as the calendar LISTS it. Berkeley Time is applied on the NATIVE side.
 *
 * `startsAt`/`endsAt` are wall-clock ISO-8601 with no zone (`2026-09-15T10:00:00`) because that
 * is all this app has: `ClassOccurrence` is a date plus minutes-into-the-day, deliberately, so a
 * phone whose clock is on the wrong timezone cannot mislabel a lecture. The plugin resolves them
 * against `zone` — `America/Los_Angeles` unless something says otherwise — which is where the
 * daylight-saving arithmetic lives, in one place, with tests.
 */
export interface NativeScheduleOccurrence {
  courseCode: string
  startsAt: string
  endsAt: string
  location?: string | null
  zone?: string | null
}

/** One armed alarm pair, read back off the phone rather than assumed. Instants, in UTC. */
export interface NativeScheduledClass {
  courseCode: string
  /** When recording will actually START: listed start + Berkeley Time. */
  startsAt: string
  /**
   * When it will STOP: listed end + the end offset (3 minutes — classes end on the listed time;
   * an alarm armed by an APK from before 2026-10-15 still says + 10, and this is what it says).
   */
  endsAt: string
  location?: string | null
}

export interface NativeSchedule {
  scheduled: NativeScheduledClass[]
  /**
   * FALSE MEANS THE ALARMS MAY FIRE LATE, by an amount Android does not promise. It is surfaced
   * rather than smoothed over, because "your phone will record your 10:10" and "your phone will
   * probably record most of your 10:10" are different promises.
   */
  exactAlarms: boolean
  /** Why, in the platform's terms, when `exactAlarms` is false. */
  reason?: string | null
}

/**
 * Arm the class alarms. Idempotent BY REPLACEMENT — everything previously armed is cancelled.
 *
 * The screen calls this whenever the day's occurrences load, which is also what re-arms them
 * after a reinstall. A merged schedule would be a schedule nobody could reason about once a class
 * was cancelled, so there is no merge.
 *
 * THE END OFFSET IS ALWAYS SENT (2026-10-15). The stop moved from listed end + 10 to + 3, and the
 * web app ships without an APK rebuild: sending it means the number in `class-labeling.ts` is the
 * one the phone uses, on any build that reads it. An APK from before ignores the field and keeps
 * stopping at + 10 — which `nativeScheduledClasses()` then reports honestly, because it reads
 * back the phone's own instants. The START offset is still left to the plugin's default.
 */
export async function scheduleNativeClasses(
  occurrences: readonly NativeScheduleOccurrence[],
  berkeleyOffsetMinutes?: number,
  endOffsetMinutes: number = DEFAULT_END_OFFSET_MINUTES,
): Promise<NativeResult<NativeSchedule>> {
  return normalizeSchedule(
    await call<NativeSchedule>('plugin:lifeos-recorder|schedule', {
      payload: {
        occurrences,
        ...(berkeleyOffsetMinutes === undefined ? {} : { berkeleyOffsetMinutes }),
        endOffsetMinutes,
      },
    }),
  )
}

/** What the phone will actually do, read back. Never re-derived from the timetable. */
export async function nativeScheduledClasses(): Promise<NativeResult<NativeSchedule>> {
  return normalizeSchedule(await call<NativeSchedule>('plugin:lifeos-recorder|scheduled'))
}

function normalizeSchedule(answer: NativeResult<NativeSchedule>): NativeResult<NativeSchedule> {
  if (!answer.available) return answer
  const value = answer.value
  if (!value || !Array.isArray(value.scheduled) || typeof value.exactAlarms !== 'boolean') {
    return { available: false, reason: 'The app answered with something that is not a schedule.' }
  }
  return { available: true, value }
}

/** '20260913T210407Z-cogsci-1.m4a' out of the plugin's absolute path. Never an invented name. */
export function nativeRecordingName(path: string): string {
  const parts = path.split(/[\\/]/)
  const last = parts[parts.length - 1]
  return last && last.length > 0 ? last : path
}
